import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from '@/lib/auth';
import Login from '@/pages/Login';
import DepartmentSelector from '@/pages/DepartmentSelector';
import AppShell from '@/pages/AppShell';
import Dashboard from '@/pages/Dashboard';
import Invoices from '@/pages/Invoices';
import InvoiceDetail from '@/pages/InvoiceDetail';
import Tasks from '@/pages/Tasks';
import Reports from '@/pages/Reports';
import Users from '@/pages/Users';
import ColumnsAdmin from '@/pages/ColumnsAdmin';
import AuditLog from '@/pages/AuditLog';
import EmployeeKPI from '@/pages/EmployeeKPI';
import MessageTemplates from '@/pages/MessageTemplates';
import EmailDigest from '@/pages/EmailDigest';
import Backups from '@/pages/Backups';
import Letters from '@/pages/Letters';
import LegalDashboard from '@/pages/LegalDashboard';
import LegalClaims from '@/pages/LegalClaims';
import LegalClaimDetail from '@/pages/LegalClaimDetail';
import LegalCases from '@/pages/LegalCases';
import LegalSettings from '@/pages/LegalSettings';
import '@/App.css';

const Protected = ({ children }) => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

const DeptGuard = ({ department, children }) => {
  const { user, currentDepartment } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (!currentDepartment) return <Navigate to="/select-department" replace />;
  const userDepts = user.departments || (user.role === 'admin' ? ['collections', 'legal'] : ['collections']);
  if (!userDepts.includes(department) && user.role !== 'admin') {
    return <Navigate to="/select-department" replace />;
  }
  return children;
};

const Home = () => {
  const { user, currentDepartment } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (!currentDepartment) return <Navigate to="/select-department" replace />;
  // Invoices is the primary screen for collections
  return <Navigate to={currentDepartment === 'legal' ? '/legal' : '/invoices'} replace />;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-center" richColors dir="rtl" />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/select-department" element={<Protected><DepartmentSelector /></Protected>} />

          {/* Collections workspace */}
          <Route path="/" element={<DeptGuard department="collections"><AppShell department="collections" /></DeptGuard>}>
            <Route index element={<Home />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="invoices" element={<Invoices />} />
            <Route path="invoices/:id" element={<InvoiceDetail />} />
            <Route path="tasks" element={<Tasks />} />
            <Route path="kpi/agents" element={<EmployeeKPI />} />
            <Route path="templates" element={<MessageTemplates />} />
            <Route path="digest" element={<EmailDigest />} />
            <Route path="letters" element={<Letters />} />
            <Route path="backups" element={<Backups />} />
            <Route path="reports" element={<Reports />} />
            <Route path="users" element={<Users />} />
            <Route path="columns" element={<ColumnsAdmin />} />
            <Route path="audit" element={<AuditLog />} />
          </Route>

          {/* Legal workspace */}
          <Route path="/legal" element={<DeptGuard department="legal"><AppShell department="legal" /></DeptGuard>}>
            <Route index element={<LegalDashboard />} />
            <Route path="claims" element={<LegalClaims />} />
            <Route path="claims/:id" element={<LegalClaimDetail />} />
            <Route path="cases" element={<LegalCases />} />
            <Route path="settings" element={<LegalSettings />} />
            <Route path="audit" element={<AuditLog />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
